import {
  WorkflowNotificationContextProfile,
  WorkflowNotificationMessage,
  WorkflowNotificationVariable,
} from "./platform-workflow-definition-lifecycle.models";

export type NotificationMessageEventKind = "transition" | "decision";
export type NotificationMessageField = "message" | "context" | "title" | "body";

export interface NotificationMessageIssue {
  field: NotificationMessageField;
  code: string;
  message: string;
}

export interface NotificationMessagePreview {
  title: string | null;
  body: string | null;
  errors: NotificationMessageIssue[];
  missingVariables: string[];
  isSample: boolean;
}

const TOKEN = /\{\{([^{}]*)\}\}/g;
const VARIABLE_CODE = /^[A-Za-z][A-Za-z0-9_.]*$/;
const EVENT_KIND_CODES = { transition: "status_transition", decision: "decision_recorded" };

export function compatibleNotificationProfiles(
  profiles: WorkflowNotificationContextProfile[],
  subjectTypeCode: string,
  eventKind: NotificationMessageEventKind,
): WorkflowNotificationContextProfile[] {
  return profiles.filter(profile => (!profile.subjectTypeCodes.length || (!!subjectTypeCode.trim() && profile.subjectTypeCodes.includes(subjectTypeCode)))
    && (!profile.eventKindCodes?.length || profile.eventKindCodes.includes(EVENT_KIND_CODES[eventKind])));
}

export function resolveNotificationMessageProfile(
  message: WorkflowNotificationMessage,
  profiles: WorkflowNotificationContextProfile[],
  subjectTypeCode: string,
  eventKind: NotificationMessageEventKind,
): WorkflowNotificationContextProfile | null {
  const matches = compatibleNotificationProfiles(profiles, subjectTypeCode, eventKind)
    .filter(profile => profile.code === message.contextProfileCode
      && profile.version === message.contextProfileVersion);
  return matches.length === 1 ? matches[0] : null;
}

export function availableNotificationVariables(
  profile: WorkflowNotificationContextProfile | null,
  eventKind: NotificationMessageEventKind,
): WorkflowNotificationVariable[] {
  return (profile?.variables ?? []).filter(variable => !variable.eventKindCodes?.length
    || variable.eventKindCodes.includes(EVENT_KIND_CODES[eventKind]));
}

export function notificationMessageVariableCodes(template: string): string[] {
  if (typeof template !== "string") return [];
  return [...new Set(Array.from(template.matchAll(TOKEN), match => match[1])
    .filter(code => VARIABLE_CODE.test(code)))];
}

export function validateNotificationMessage(
  message: WorkflowNotificationMessage | null,
  profiles: WorkflowNotificationContextProfile[],
  subjectTypeCode: string,
  eventKind: NotificationMessageEventKind,
): NotificationMessageIssue[] {
  if (message === null) return [];
  if (message.version !== 1) {
    return [{ field: "message", code: "unsupported_version", message: `Message version ${String(message.version)} cannot be edited by this editor. Keep the saved content and use a compatible editor.` }];
  }
  const issues: NotificationMessageIssue[] = [];
  const profile = resolveNotificationMessageProfile(message, profiles, subjectTypeCode, eventKind);
  if (!profile) {
    issues.push({ field: "context", code: "unsupported_profile", message: "Choose an available context profile and version for this workflow subject and event. The saved profile is unavailable or ambiguous." });
  }
  const variables = new Set(availableNotificationVariables(profile, eventKind).map(variable => variable.code));
  for (const field of ["title", "body"] as const) {
    const value = field === "title" ? message.titleTemplate : message.bodyTemplate;
    const label = field === "title" ? "Title" : "Message body";
    const limit = field === "title" ? 200 : 8000;
    if (typeof value !== "string" || !value.trim()) {
      issues.push({ field, code: "required", message: `${label} is required when a message is configured.` });
      continue;
    }
    if (value.length > limit) {
      issues.push({ field, code: "max_length", message: `${label} must be ${limit.toLocaleString("en-US")} characters or fewer, including variables.` });
    }
    const matches = Array.from(value.matchAll(TOKEN));
    if (matches.some(match => !VARIABLE_CODE.test(match[1]))
      || /[{}]/.test(value.replace(TOKEN, ""))) {
      issues.push({ field, code: "token_syntax", message: `${label} contains invalid variable syntax. Insert a supported {{variable.code}} token; expressions, spaces inside tokens and other braces are not supported.` });
    }
    if (profile) {
      for (const code of notificationMessageVariableCodes(value)) {
        if (!variables.has(code)) {
          issues.push({ field, code: "unknown_variable", message: `${label} uses {{${code}}}, which is unavailable for this context and event. Choose a supported variable or remove it.` });
        }
      }
    }
  }
  return issues;
}

/** Plain-text, single-pass preview. A missing value or error suppresses the whole message. */
export function previewNotificationMessage(
  message: WorkflowNotificationMessage,
  profiles: WorkflowNotificationContextProfile[],
  subjectTypeCode: string,
  eventKind: NotificationMessageEventKind,
  values?: Record<string, string | null | undefined>,
): NotificationMessagePreview {
  const errors = validateNotificationMessage(message, profiles, subjectTypeCode, eventKind);
  const result: NotificationMessagePreview = {
    title: null, body: null, errors, missingVariables: [], isSample: values === undefined,
  };
  if (errors.length) return result;
  const profile = resolveNotificationMessageProfile(message, profiles, subjectTypeCode, eventKind);
  const context = values ?? Object.fromEntries(availableNotificationVariables(profile, eventKind)
    .map(variable => [variable.code, variable.sampleValue]));
  const used = [...new Set([
    ...notificationMessageVariableCodes(message.titleTemplate),
    ...notificationMessageVariableCodes(message.bodyTemplate),
  ])];
  result.missingVariables = used.filter(code => typeof context[code] !== "string" || !context[code]?.trim());
  if (result.missingVariables.length) {
    errors.push({ field: "context", code: "missing_context", message: `Message withheld: required context is missing for ${result.missingVariables.map(code => `{{${code}}}`).join(", ")}. Supply the event snapshot before rendering.` });
    return result;
  }
  if (used.some(code => (context[code]?.length ?? 0) > 8000)) {
    errors.push({ field: "context", code: "context_too_long", message: "Message withheld: a contextual value exceeds 8,000 characters." });
    return result;
  }
  const render = (template: string) => template.replace(TOKEN, (_token, code: string) => context[code] ?? "");
  const title = render(message.titleTemplate);
  const body = render(message.bodyTemplate);
  if (title.length > 1000 || body.length > 32000) {
    errors.push({ field: "message", code: "render_too_long", message: "Message withheld: the rendered title exceeds 1,000 characters or the body exceeds 32,000 characters. Shorten the template or contextual values." });
    return result;
  }
  result.title = title;
  result.body = body;
  return result;
}
